// The tree of possible timelines, as one of the right column's panels.
//
// **A node is a decision about which unit goes next.** Not which *step*: the
// scheduler's forks are per memory access, and almost all of them reorder things
// that cannot change where the program ends up — `bank.stm` has 91 of them and
// not one is a real decision, because all of its sharing is inside `txn`. A node
// here is one entry of the enumeration's own walk (`../explore.ts`): a committed
// transaction or a bare access, let in ahead of the others. See
// docs/design/txn-granular-exploration.md.
//
// **So the tree is exact.** Each node's badge is the set of endings reachable
// below it, computed bottom-up from its children rather than sampled by running
// eight ways down and hoping: `=`, not `≥`. And a set of endings is a *claim*
// and the verdicts it took, not a list of strings — a node that reads
// `= {balance reflects both withdrawals: {✗}}` is a decision that has *already*
// lost the update, whatever happens next, which is the question this panel
// exists to answer and the one sampling could never answer.
//
// **And it is small.** `bank`, `counter`, `map_reduce` and `merge` are a single
// row — nothing to decide, one outcome — where the step-level tree had hundreds.
// `lost_update.stm` is 22 nodes and shows exactly where its two outcomes split.
//
// **It is computed in a worker**, streamed in as it is walked, so the panel
// draws a tree that grows instead of freezing the page for the seconds it takes
// (opening this panel used to be a single 19-second task on `bank.stm`). Until
// the walk finishes, a node's set is what has been found below it so far and
// says `≥`; when its subtree closes it flips to `=`.

import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { RunResult } from "../../src/backend/interp/interpreter.js";
import { Tree, TreeNode } from "./Tree.js";
import { onPath } from "../timeline.js";
import { assertIndex, resolveOutcomes, summarize } from "../explore.js";
import type { ExploreNode, ExploreResult, NodeOutcomes, OutcomeSummary, Progress, UnitDid } from "../explore.js";
import { CANCELLED, exploreInWorker, fixInWorker } from "../worker-client.js";
import type { Exploration } from "../worker-client.js";
import type { Fix } from "../worker.js";
import type { RunParams } from "../race-oracle.js";
import { describeKey } from "../trace-layout.js";
import type { ObjectTypes } from "../trace-layout.js";

interface TimelinePanelProps {
  /** The run on screen, whatever produced it — the path drawn as current. */
  result: RunResult | null;
  /** The program the tree is of: the source and parameters of the run itself,
   *  so the tree answers for what is on screen. Null before anything has run. */
  program: { source: string; params: RunParams } | null;
  /** Invalidates everything: the program, seed, or strategy changed, so the
   *  walk that was running is a walk of something else. */
  cacheKey: string;
  /** The prefix currently forced, or null when the scheduler is choosing. */
  branch: number[] | null;
  /** Whether a drag's reorderings are also in force, which makes the displayed
   *  run something no prefix alone describes. */
  reordered: boolean;
  onSelectBranch(prefix: number[] | null): void;
  /** Replace the editor's source with the patched version and re-run — the one
   *  claim no static check can make, so it is offered only once the patched
   *  program has been run and its outcomes seen to collapse. */
  onApplyFix(fixed: string): void;
  /** Reveal a line in the editor, for the lines a fix would wrap. */
  onReveal(line: number): void;
  types?: ObjectTypes;
}

/** Categorical colours for outcomes, drawn from the op palette. The first two —
 *  teal, violet — carry no good/bad connotation, so a two-outcome program (the
 *  common case) is coloured neutrally; red and green sit last, where a program
 *  with many endings will reach them but a binary fork never does. */
const OUTCOME_HUES = 6;

/** How the walk is going. It starts itself when a program is run: the tree *is*
 *  the walk, so a panel that waited to be asked would have nothing to show. */
type Walk =
  | { status: "idle" }
  | { status: "walking"; progress: Progress | null }
  | { status: "done"; result: ExploreResult }
  | { status: "failed"; message: string };

export function TimelinePanel({
  result,
  program,
  cacheKey,
  branch,
  reordered,
  onSelectBranch,
  onApplyFix,
  onReveal,
  types,
}: TimelinePanelProps) {
  const [walk, setWalk] = useState<Walk>({ status: "idle" });
  // The tree as it arrives. Held in a ref and mutated, with a counter to ask for
  // a render: a hundred nodes stream in over a couple of seconds, and copying
  // the array per batch to satisfy React's identity check would be the one part
  // of this that scales badly.
  const nodes = useRef<ExploreNode[]>([]);
  const [, rerender] = useState(0);
  const running = useRef<Exploration | null>(null);
  const hues = useRef(new Map<string, number>());
  // The `txn` that removes the race, once there is a race to remove. Undefined
  // while it is being worked out (another few dozen runs, in a worker of its
  // own), null when there is nothing in the user's program to wrap.
  const [fix, setFix] = useState<Fix | null | undefined>(undefined);

  /** Start (or restart) the walk. Cancels whatever was running: its answers are
   *  about a program that is no longer the one on screen. */
  const enumerate = (exhaustive = false): void => {
    running.current?.cancel();
    running.current = null;
    nodes.current = [];
    if (!program) {
      setWalk({ status: "idle" });
      return;
    }
    setWalk({ status: "walking", progress: null });
    setFix(undefined);
    const started = exploreInWorker(program.source, program.params, {
      exhaustive,
      onProgress: (progress, found) => {
        nodes.current = [...nodes.current, ...found];
        setWalk((w) => (w.status === "walking" ? { ...w, progress } : w));
        rerender((n) => n + 1);
      },
    });
    running.current = started;
    started.result.then(
      (r) => {
        running.current = null;
        nodes.current = r.nodes;
        setWalk({ status: "done", result: r });
        // A race is a question the reader will ask next: what should have been
        // atomic? That costs more runs than the walk did, so it is asked for
        // only when there is one, and only after the tree is on screen.
        if (r.race) {
          fixInWorker(program.source, program.params, r.race.fails.schedule).then(
            (found) => setFix(found),
            () => setFix(null)
          );
        } else {
          setFix(null);
        }
      },
      (e: Error) => {
        running.current = null;
        // Cancelling is not a failure — it is the answer to "stop".
        setWalk(e.message === CANCELLED ? { status: "idle" } : { status: "failed", message: e.message });
      }
    );
  };

  // One walk per program. `cacheKey` changes exactly when a prefix would mean
  // something else, which is exactly when the tree is of the wrong program.
  useEffect(() => {
    enumerate(false);
    return () => {
      running.current?.cancel();
      running.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  const stop = (): void => {
    running.current?.cancel();
    running.current = null;
    setWalk({ status: "idle" });
  };

  const all = nodes.current;
  // Resolved on every render rather than cached: it is a walk over a hundred
  // nodes with a memo, and it has to be redone whenever the tree grows anyway.
  const sets = resolveOutcomes(all);
  const c = result?.causality ?? null;

  /** The palette class for an outcome — assigned once, in first-seen order, and
   *  stable for the life of the program so the colour means the same everywhere. */
  const outcomeClass = (o: string): string => {
    if (!hues.current.has(o)) hues.current.set(o, hues.current.size);
    return `tl-out-${hues.current.get(o)! % OUTCOME_HUES}`;
  };

  /** What a thread does with the turn this node gives it, in the trace view's
   *  own vocabulary: an op and the key it touches. A transaction shows its
   *  footprint, because that — not any one access — is what it does atomically. */
  const didLabel = (did: UnitDid | null): ReactElement | null => {
    if (!did) return null;
    const keys = did.addrs.map((a) => describeKey(a, types));
    if (did.op === "txn") {
      return (
        <>
          <span className="tl-did">txn</span>
          {keys.length > 0 && <span className="tl-foot">{`{${keys.join(", ")}}`}</span>}
        </>
      );
    }
    return (
      <span className="tl-did">
        {did.op}
        {keys.length > 0 ? ` ${keys[0]}` : ""}
      </span>
    );
  };

  /** The claims behind a set of labels — built once per render, read per row. */
  const claimsOf = assertIndex(all);

  /**
   * A set of endings, said once.
   *
   * A program's endings are not really a list of strings; they are a *claim* and
   * the verdicts it took. `{no increment lost: {✓, ✗}}` says in one line what
   * `{✓ no increment lost, ✗ no increment lost}` said twice — and with two
   * assertions it is the one that actually splits that you see split. Endings
   * with no assertion behind them (a `print`) keep their own text and their own
   * colour, since there is no verdict to fold.
   *
   * `pick` makes each verdict a button — the bar wants that (each verdict names
   * a schedule to open); a tree row does not, because the row itself is already
   * the thing you click.
   */
  const OutcomeSet = ({
    summary,
    pick,
  }: {
    summary: OutcomeSummary;
    pick?: (verdict: "pass" | "fail") => void;
  }): ReactElement => {
    const parts: ReactElement[] = [];
    for (const claim of summary.claims) {
      parts.push(
        <span className="tl-claim" key={`c:${claim.message}`}>
          {claim.message && <span className="tl-claim-msg">{claim.message}: </span>}
          <span className="tl-brace">{"{"}</span>
          {claim.verdicts.map((v, i) => (
            <span key={v}>
              {i > 0 && <span className="tl-comma">, </span>}
              {pick ? (
                <button
                  className={`tl-verd ${v}`}
                  title={v === "pass" ? "open a schedule where this holds" : "open a schedule where this fails"}
                  onClick={() => pick(v)}
                >
                  {v === "pass" ? "✓" : "✗"}
                </button>
              ) : (
                <span className={`tl-verd ${v}`}>{v === "pass" ? "✓" : "✗"}</span>
              )}
            </span>
          ))}
          <span className="tl-brace">{"}"}</span>
        </span>
      );
    }
    for (const label of summary.plain) {
      parts.push(
        <span className={`tl-outcome ${outcomeClass(label)}`} key={`p:${label}`}>
          {label}
        </span>
      );
    }
    return (
      <span className="tl-set">
        <span className="tl-brace">{"{"}</span>
        {parts.map((part, i) => (
          <span key={i}>
            {i > 0 && <span className="tl-comma">, </span>}
            {part}
          </span>
        ))}
        <span className="tl-brace">{"}"}</span>
      </span>
    );
  };

  /** The set of endings reachable below a node, with the sigil that says whether
   *  it is the whole of them. */
  const outcomeSet = (node: ExploreNode, below: NodeOutcomes): ReactElement | null => {
    // A program that neither asserts nor prints has no ending to name — the tree
    // used to guess one from the heap, which was longer and less true.
    const summary = summarize(below.outcomes, claimsOf);
    if (summary.claims.length === 0 && summary.plain.length === 0) return null;
    return (
      <>
        <span
          className={`tl-bound${below.exact ? " exact" : ""}`}
          title={
            below.exact
              ? "every ordering below this decision was enumerated: these outcomes, and no others"
              : "found below this decision so far — the walk has not finished here yet"
          }
        >
          {below.exact ? "=" : "≥"}
        </span>
        <OutcomeSet summary={summary} />
      </>
    );
  };

  /** Children of a node, in tie-break order — `children[0]` is the pick the
   *  unforced scheduler makes, which is the path the program takes on its own. */
  const childrenOf = (node: ExploreNode): ExploreNode[] =>
    node.children.map((id) => all[id]).filter((n): n is ExploreNode => !!n);

  /** Whether this node is on the default order's own path: the chain of first
   *  children down from the root. Fixed by the program rather than by what is
   *  selected, so opening a node never reshapes the tree under the cursor. */
  const onDefaultPath = (node: ExploreNode): boolean => {
    let cursor = all[0];
    while (cursor) {
      if (cursor.id === node.id) return true;
      cursor = all[cursor.children[0] ?? -1];
    }
    return false;
  };

  const treeNode = (node: ExploreNode): TreeNode => {
    const below = sets.get(node.id) ?? { outcomes: [], exact: false };
    const kids = childrenOf(node);
    const current = !!c && !reordered && onPath(c, node.prefix);
    const repeat = node.repeatOf !== null;
    return {
      id: `n${node.id}`,
      leaf: node.settled || repeat,
      count: kids.length > 0 ? kids.length : undefined,
      selected: !!branch && branch.join(",") === node.prefix.join(","),
      className: current ? "tl-on" : undefined,
      defaultExpanded: onDefaultPath(node),
      onSelect: () => onSelectBranch(node.prefix.length > 0 ? node.prefix : null),
      label: (
        <>
          {node.thread === null ? (
            <span className="tl-start">program start</span>
          ) : (
            <span className="tl-thread">T{node.thread}</span>
          )}
          {didLabel(node.did)}
          {repeat && (
            <span
              className="tl-repeat"
              title="the same position reached another way — same committed heap, same progress per thread — so whatever is reachable below it is reachable below the node it repeats"
            >
              ≡
            </span>
          )}
          {node.settled && (
            <span className="tl-settled" title="nothing left to decide: one timeline from here">
              settled
            </span>
          )}
          {outcomeSet(node, below)}
        </>
      ),
      children: () => kids.map(treeNode),
    };
  };

  /**
   * The `txn` the race is missing — offered only once the patched program has
   * been run and its outcomes seen to collapse to one.
   *
   * That verification is the whole reason this is worth offering at all: "wrap
   * these lines" is a guess a linter could make, and "wrap these lines and the
   * two outcomes become one" is a fact about *this* program. A fix that spans
   * two functions, or that does not collapse them, is reported rather than
   * offered — a button that silently does the wrong thing is worse than none.
   */
  const fixOffer = (): ReactElement | null => {
    if (fix === undefined) return <span className="tl-hint">looking for the fix…</span>;
    if (fix === null) return null;
    const lines = fix.lineA === fix.lineB ? `line ${fix.lineA}` : `lines ${fix.lineA}–${fix.lineB}`;
    if (!fix.verified.verified) {
      return (
        <span className="tl-hint" title="the splice would straddle a block, or the outcomes did not collapse when it was tried">
          {lines} are not atomic
        </span>
      );
    }
    return (
      <button
        className="tl-fix"
        title={`wrap ${lines} in txn — run patched, the ${fix.verified.before.size} outcomes collapse to ${fix.verified.after.size}`}
        onClick={() => {
          onReveal(fix.lineA);
          onApplyFix(fix.verified.fixed);
        }}
      >
        wrap {lines} in <code>txn</code>
      </button>
    );
  };

  /** The walk's own state — how far it got, and whether the tree below it is a
   *  proof or a floor. */
  const bar = (): ReactElement | null => {
    if (!program) return null;
    if (walk.status === "walking") {
      const p = walk.progress;
      return (
        <div className="tl-explore">
          <button className="tl-enumerate" onClick={stop}>
            stop
          </button>
          <span className="tl-progress">
            enumerating…{p ? ` ${p.runs} runs, ${all.length} decision${all.length === 1 ? "" : "s"}` : ""}
          </span>
        </div>
      );
    }
    if (walk.status === "failed") {
      return (
        <div className="tl-explore">
          <button className="tl-enumerate" onClick={() => enumerate(false)}>
            try again
          </button>
          <span className="tl-progress">enumeration failed: {walk.message}</span>
        </div>
      );
    }
    if (walk.status === "idle") {
      return (
        <div className="tl-explore">
          <button className="tl-enumerate" onClick={() => enumerate(false)}>
            enumerate
          </button>
          <span className="tl-hint">every ordering of transactions and bare accesses — a set, not a sample</span>
        </div>
      );
    }
    const r = walk.result;
    const runs = r.runs === 1 ? "1 run" : `${r.runs} runs`;
    return (
      <div className="tl-explore">
        <button className="tl-enumerate" onClick={() => enumerate(false)}>
          re-enumerate
        </button>
        {/* When the program asserts something, that is the headline — the walk
            was looking for an ordering that breaks the claim, and either it found
            one (with the ordering that keeps it, alongside) or it got to the end
            without one, which is the stronger statement of the two. */}
        {r.race ? (
          <>
            <span className="tl-verdict fail" title="the assertion holds under one ordering and fails under another: a race">
              races
            </span>
            {/* The same set the rows carry, with the verdicts made clickable:
                each one names a schedule that reaches it. */}
            <OutcomeSet
              summary={summarize([r.race.fails.label, r.race.passes.label], claimsOf)}
              pick={(verdict) => {
                const schedule = (verdict === "pass" ? r.race!.passes : r.race!.fails).schedule;
                onSelectBranch(schedule.length > 0 ? schedule : null);
              }}
            />
            <button
              className="tl-enumerate"
              title="the race is the answer, so the walk stopped there — this asks for the rest of the outcome set as well"
              onClick={() => enumerate(true)}
            >
              keep going
            </button>
            {fixOffer()}
          </>
        ) : r.verdict === "pass" && r.complete ? (
          <span
            className="tl-verdict pass"
            title={`every ordering of this program's ${r.units} units was walked, in ${r.runs} runs, and the assertions held in all of them`}
          >
            holds under every ordering
          </span>
        ) : r.verdict === "fail" && r.complete ? (
          <span className="tl-verdict fail" title="no ordering was found where the assertions hold">
            fails under every ordering
          </span>
        ) : null}
        <span
          className="tl-progress"
          title={
            r.race
              ? `stopped at ${runs}: both verdicts are in hand, and nothing further down makes the race more true`
              : r.complete
                ? `every ordering of this program's ${r.units} units was walked, in ${runs}. The sets below are exact`
                : `a lower bound: ${r.incomplete.join(", ")}`
          }
        >
          {runs}
          {r.complete || r.race ? "" : ` — incomplete (${r.incomplete.join(", ")})`}
        </span>
      </div>
    );
  };

  /**
   * What an empty tree means — which is not one thing.
   *
   * "nothing to decide: one thread, one timeline" is a *claim about the
   * program*: the walk ran, reached the end, and there was exactly one
   * ordering to find. A walk that failed, or one that was stopped before it
   * found anything, has made no such claim — the tree is unknown, not empty,
   * and saying otherwise reports a crash as a clean bill of health. Only
   * `done` earns the claim; the other states say why there is nothing here.
   */
  const emptyText = (): string => {
    if (!program) return "run a program to explore its schedules";
    switch (walk.status) {
      case "walking":
        return "enumerating…";
      case "failed":
        return "enumeration failed — no timelines to show";
      case "idle":
        return "not enumerated — no timelines to show";
      case "done":
        return "nothing to decide: one thread, one timeline";
    }
  };

  const root = all[0];
  return (
    <div id="timeline">
      {bar()}
      {/* The way back, with the tree rather than in the panel header: a header
          is a title and a fold. */}
      {branch && (
        <div className="tl-bar">
          <button className="tl-reset" onClick={() => onSelectBranch(null)}>
            back to the default order
          </button>
        </div>
      )}
      <Tree
        nodes={root ? [treeNode(root)] : []}
        ariaLabel="tree of possible timelines"
        indent={11}
        empty={emptyText()}
      />
    </div>
  );
}
